import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { z } from "zod";
import { DRI_SUPPLY_SECTION } from "./parameterSections";
import { useTenantData } from "contexts/tenant";
import { useTenant } from "hooks/settings";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useFormatNumber } from "src/utils/formatNumber";
import { useDebouncedRequest } from "hooks/useDebouncedRequest";
import { useElementSize } from "hooks/useElementSize";
import LocalisedPlot from "components/plotly/LocalisedPlot";
import {
  AutoRecipeDefaultsContext,
  AutoRecipeSupplyPreview,
  AutoRecipeParameters,
  AutoRecipeParameterScope,
  usePreviewAutoRecipeSupplyMutation,
} from "store/api/generatedApi";

const rejectionSchema = z.object({
  status: z.union([z.literal(409), z.literal(422)]),
});
interface SupplyPreviewRequest {
  tenantName: string;
  scope: AutoRecipeParameterScope;
  context: AutoRecipeDefaultsContext;
  supplyParameters: string;
  startTimestamp: string;
  endTimestamp: string;
}
type PreviewResponse =
  | { status: "accepted"; production: AutoRecipeSupplyPreview }
  | {
      status: "rejected";
      reason: "parameters" | "stale";
      parameters: AutoRecipeParameters;
    };

export const SupplyPreviewPlot = ({
  context,
  parameters,
  scope,
  referenceTimestamp,
  isValid,
}: {
  context: AutoRecipeDefaultsContext;
  parameters: AutoRecipeParameters;
  scope: AutoRecipeParameterScope;
  referenceTimestamp: string | null;
  isValid: boolean;
}) => {
  const { t } = useTenantTranslation();
  const { local_timezone: timezone } = useTenantData();
  const tenantName = useTenant();
  const theme = useTheme();
  const units = useUnitsFormatter(false);
  const formatNumber = useFormatNumber()(1);
  const { ref, size } = useElementSize();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const date =
    selectedDate ??
    (referenceTimestamp
      ? dayjs(referenceTimestamp).tz(timezone).format("YYYY-MM-DD")
      : "");
  const [preview] = usePreviewAutoRecipeSupplyMutation();
  const latestParametersRef = useRef(parameters);
  useEffect(() => {
    latestParametersRef.current = parameters;
  }, [parameters]);
  const supplyParameters = JSON.stringify(
    parameters[DRI_SUPPLY_SECTION] ?? null
  );
  const request = useMemo((): SupplyPreviewRequest | null => {
    if (!isValid || !date || !dayjs(date).isValid()) return null;
    const endDate = dayjs(date).add(7, "day").format("YYYY-MM-DD");
    return {
      tenantName,
      scope,
      context,
      supplyParameters,
      startTimestamp: dayjs.tz(date, timezone).toISOString(),
      endTimestamp: dayjs.tz(endDate, timezone).toISOString(),
    };
  }, [isValid, date, tenantName, scope, context, supplyParameters, timezone]);
  const check = useCallback(
    (request: SupplyPreviewRequest) => {
      const submittedParameters = latestParametersRef.current;
      const pending = preview({
        tenantName: request.tenantName,
        scope: request.scope,
        autoRecipeSupplyPreviewRequest: {
          context: request.context,
          start_timestamp: request.startTimestamp,
          end_timestamp: request.endTimestamp,
          parameters: submittedParameters,
        },
      });
      return {
        response: pending.unwrap().then(
          (production): PreviewResponse => ({ status: "accepted", production }),
          (error: unknown): PreviewResponse => {
            const rejected = rejectionSchema.safeParse(error);
            if (!rejected.success) throw error;
            return {
              status: "rejected",
              reason: rejected.data.status === 409 ? "stale" : "parameters",
              parameters: submittedParameters,
            };
          }
        ),
        abort: () => {
          pending.abort();
        },
      };
    },
    [preview]
  );
  const pending = useDebouncedRequest({ request, check, delay: 300 });
  const response =
    pending.isCurrent && pending.completion?.status === "success"
      ? pending.completion.response
      : null;
  const rejection = response?.status === "rejected" ? response : null;
  const retry = pending.retry;
  useEffect(() => {
    if (
      rejection?.reason === "parameters" &&
      rejection.parameters !== parameters
    )
      retry();
  }, [rejection, parameters, retry]);
  const production =
    response?.status === "accepted" ? response.production : null;
  const ticks = Array.from(
    new Set(
      production?.points.map(({ timestamp }) =>
        dayjs(timestamp).tz(timezone).format("YYYY-MM-DD")
      ) ?? []
    )
  ).map((date) => dayjs.tz(date, timezone));
  return (
    <Stack gap={1}>
      <Typography variant="subtitle1">{t("autoSupplyPreview")}</Typography>
      <TextField
        label={t("autoSupplyPreviewStart")}
        type="date"
        value={date}
        slotProps={{ inputLabel: { shrink: true } }}
        onChange={(event) => {
          setSelectedDate(event.target.value);
        }}
      />
      <Typography variant="body2" color="text.secondary">
        {t("autoLocalTime", { timezone })}
      </Typography>
      {request === null ? (
        <Alert severity="info">{t("autoSupplyPreviewNeedsInputs")}</Alert>
      ) : null}
      {rejection ? (
        <Alert severity="warning">
          {t(
            rejection.reason === "stale"
              ? "autoSupplyPreviewStale"
              : "autoSupplyPreviewInvalid"
          )}
        </Alert>
      ) : null}
      {pending.isChecking ? (
        <Stack gap={1} role="status">
          <Typography variant="body2">
            {t("autoSupplyPreviewLoading")}
          </Typography>
          <LinearProgress />
        </Stack>
      ) : null}
      {pending.hasError ? (
        <Alert
          severity="warning"
          action={<Button onClick={pending.retry}>{t("retry")}</Button>}
        >
          {t("autoSupplyPreviewFailed")}
        </Alert>
      ) : null}
      {production ? (
        <>
          <Typography variant="body2Mono">
            {t("autoSupplyPreviewTotal", {
              mass: formatNumber(production.total_mass),
              unit: units("mass", false),
            })}
          </Typography>
          <Box ref={ref} sx={{ "& .js-plotly-plot": { width: "100%" } }}>
            <LocalisedPlot
              useResizeHandler
              data={[
                {
                  type: "bar",
                  name: t("autoSupplyPreview"),
                  x: production.points.map(({ timestamp }) =>
                    dayjs(timestamp).valueOf()
                  ),
                  y: production.points.map(({ mass }) => mass),
                  hovertext: production.points.map(
                    ({ timestamp, mass }) =>
                      `${dayjs(timestamp).tz(timezone).format("DD MMM HH:mm Z")}: ${formatNumber(mass)} ${units("mass", false)}`
                  ),
                  hoverinfo: "text",
                  marker: { color: theme.palette.primary.main },
                },
              ]}
              layout={{
                height: 280,
                autosize: true,
                width: size?.width,
                margin: { l: 60, r: 20, t: 10, b: 60 },
                xaxis: {
                  type: "linear",
                  tickmode: "array",
                  tickvals: ticks.map((timestamp) => timestamp.valueOf()),
                  ticktext: ticks.map((timestamp) =>
                    timestamp.format("ddd DD MMM")
                  ),
                  fixedrange: true,
                },
                yaxis: {
                  title: { text: units("mass", false) },
                  rangemode: "tozero",
                  fixedrange: true,
                },
                showlegend: false,
                dragmode: false,
              }}
              config={{ displayModeBar: false }}
            />
          </Box>
        </>
      ) : null}
    </Stack>
  );
};
